import { useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { CATEGORIES, PANEL_TYPES, REFRESH_BUCKETS, RESOLUTIONS, SIZE_BUCKETS, SORT_OPTIONS } from '@/lib/constants';
import type { BrandStat } from '@/lib/api';

export interface FilterState {
  category: string;
  brand: string;
  resolution: string;
  size: string;
  /** Refresh-rate range bucket as "min-max" or "min-" string (e.g., "100-144", "280-").
   *  Empty = no filter. See REFRESH_BUCKETS in lib/constants. */
  refresh_range: string;
  /** Exact panel type label ('IPS', 'QD-OLED', ...). Empty = no filter. */
  panel_type: string;
  search: string;
  sort: string;
}

export interface FilterBarProps {
  state: FilterState;
  brands: BrandStat[];
  onChange: (patch: Partial<FilterState>) => void;
  onReset: () => void;
}

const ALL_VALUE = '__all__';

export function FilterBar({ state, brands, onChange, onReset }: FilterBarProps) {
  const searchRef = useRef<HTMLInputElement>(null);

  // Hotkey "/" focuses search; Escape clears search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (document.activeElement as HTMLElement | null)?.tagName;
      if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape' && document.activeElement === searchRef.current) {
        onChange({ search: '' });
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onChange]);

  const hasActiveFilter =
    state.category ||
    state.brand ||
    state.resolution ||
    state.size ||
    state.refresh_range ||
    state.panel_type ||
    state.search ||
    state.sort !== 'name:asc';

  return (
    <div className="space-y-3">
      {/* Search + sort row */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchRef}
            value={state.search}
            onChange={(e) => onChange({ search: e.target.value })}
            placeholder="Tìm SKU, tên sản phẩm..."
            className="pl-9 pr-12"
            autoComplete="off"
          />
          <kbd className="pointer-events-none absolute right-3 top-1/2 hidden h-5 -translate-y-1/2 select-none items-center rounded border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground sm:inline-flex">
            /
          </kbd>
        </div>

        <div className="flex items-center gap-2">
          <Select value={state.sort} onValueChange={(v) => onChange({ sort: v })}>
            <SelectTrigger className="flex-1 sm:w-[180px] sm:flex-none">
              <SelectValue placeholder="Sắp xếp" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilter && (
            <Button variant="ghost" size="sm" onClick={onReset} className="text-muted-foreground">
              <X className="h-3.5 w-3.5" />
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Category chips — horizontal scroll on mobile/tablet, wrap on desktop */}
      <div className="-mx-4 flex items-center gap-1.5 overflow-x-auto px-4 pb-1 scrollbar-none lg:-mx-0 lg:flex-wrap lg:px-0 lg:pb-0">
        {CATEGORIES.map((cat) => {
          const active = state.category === cat.value;
          const Icon = cat.icon;
          return (
            <button
              key={cat.value || 'all'}
              type="button"
              onClick={() => onChange({ category: cat.value })}
              className={cn(
                'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:bg-accent hover:text-foreground'
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {cat.label}
            </button>
          );
        })}
      </div>

      {/* Dropdowns row — 2 per row on mobile (each 50%), inline on tablet+ */}
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={state.brand || ALL_VALUE}
          onValueChange={(v) => onChange({ brand: v === ALL_VALUE ? '' : v })}
        >
          <SelectTrigger className="w-[calc(50%-0.25rem)] sm:w-[180px]">
            <SelectValue placeholder="Tất cả hãng" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_VALUE}>Tất cả hãng</SelectItem>
            {brands.map((b) => (
              <SelectItem key={b.brand} value={b.brand}>
                {b.brand} <span className="ml-1 text-muted-foreground">({b.count})</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Separator orientation="vertical" className="hidden h-6 sm:block" />

        <Select
          value={state.resolution || ALL_VALUE}
          onValueChange={(v) => onChange({ resolution: v === ALL_VALUE ? '' : v })}
        >
          <SelectTrigger className="w-[calc(50%-0.25rem)] sm:w-[160px]">
            <SelectValue placeholder="Mọi độ phân giải" />
          </SelectTrigger>
          <SelectContent>
            {RESOLUTIONS.map((r) => (
              <SelectItem key={r || ALL_VALUE} value={r || ALL_VALUE}>
                {r || 'Mọi độ phân giải'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={state.size || ALL_VALUE}
          onValueChange={(v) => onChange({ size: v === ALL_VALUE ? '' : v })}
        >
          <SelectTrigger className="w-[calc(50%-0.25rem)] sm:w-[180px]">
            <SelectValue placeholder="Mọi kích thước" />
          </SelectTrigger>
          <SelectContent>
            {SIZE_BUCKETS.map((s) => (
              <SelectItem key={s.value || ALL_VALUE} value={s.value || ALL_VALUE}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={state.refresh_range || ALL_VALUE}
          onValueChange={(v) => onChange({ refresh_range: v === ALL_VALUE ? '' : v })}
        >
          <SelectTrigger className="w-[calc(50%-0.25rem)] sm:w-[140px]">
            <SelectValue placeholder="Mọi tần số" />
          </SelectTrigger>
          <SelectContent>
            {REFRESH_BUCKETS.map((r) => (
              <SelectItem key={r.value || ALL_VALUE} value={r.value || ALL_VALUE}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={state.panel_type || ALL_VALUE}
          onValueChange={(v) => onChange({ panel_type: v === ALL_VALUE ? '' : v })}
        >
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Mọi tấm nền" />
          </SelectTrigger>
          <SelectContent>
            {PANEL_TYPES.map((p) => (
              <SelectItem key={p.value || ALL_VALUE} value={p.value || ALL_VALUE}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
